import Image from "antd/lib/image";
import message from "antd/lib/message";
import Popconfirm from "antd/lib/popconfirm";
import Spin from "antd/lib/spin";
import Typography from "antd/lib/typography";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "src/modules/uikit/data-entry/Upload/Upload";

import {
    IMediaResource,
    IMediaSecuredResource,
    MediaType as MediaResourceType,
    useMediaCreateMutation,
} from "@sol/sdk";
import { Plus01 } from "@sol/uikit/core/icons/General/Plus01";
import { Trash01 } from "@sol/uikit/core/icons/General/Trash01";
import { Button } from "@sol/uikit/general/Button/Button";
const { Dragger } = Upload;

type MediaType = IMediaResource | IMediaSecuredResource | null;

type Props = {
    className?: string;
    value: MediaType;
    onChange: (media: MediaType | undefined) => void;
    maxFileSize?: number;
    titleSectionId?: string;
    onDelete?: () => Promise<void>;
};

const MAX_FILE_SIZE = 10000000;
const SUPPORTED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const ImageUploadInput = ({
    className,
    value,
    onChange,
    maxFileSize = MAX_FILE_SIZE,
    titleSectionId,
    onDelete,
}: Props) => {
    const [t] = useTranslation();
    const [messageApi, contextHolder] = message.useMessage();
    const [previewOpen, setPreviewOpen] = useState(false);

    const handleDelete = async () => {
        if (!onDelete) {
            onChange(null);
            return;
        }

        try {
            await onDelete();
            onChange(null);
        } catch {
            messageApi.error(t("input.image.delete.error"));
        }
    };

    const { mutate: uploadAttachmentMutation, isPending } = useMediaCreateMutation({
        onSuccess: data => onChange(data),
        onError: () => {
            messageApi.error(t("component.AddMediaForm.defaultErrorMessage"));
        },
    });

    const handleUpload = async (file: File) => {
        const isLt10M = file.size / 1024 / 1024 < maxFileSize / 1000000;
        if (!isLt10M) {
            messageApi.error(
                t("component.AddMediaForm.fileTooLargeErrorMessage", { maxSize: `${maxFileSize / 1000000} Mo` }),
            );
            return false;
        }

        uploadAttachmentMutation({
            type: MediaResourceType.FILE,
            file,
        });
        return false; // Prevent default upload behavior
    };

    const handlePreview = () => {
        if (value?.url) {
            setPreviewOpen(true);
        }
    };

    const deleteButton = (
        <Button
            onClick={onDelete ? undefined : handleDelete}
            className="absolute top-4 right-4 z-element flex size-8 cursor-pointer items-center justify-center p-0 text-white transition-colors"
        >
            <Trash01 />
        </Button>
    );

    return (
        <div
            className={clsx(
                "flex w-full items-center justify-center overflow-hidden rounded-medium bg-ant-fill-tertiary",
                className,
            )}
            aria-labelledby={titleSectionId}
        >
            {value ? (
                <div className="relative flex size-full items-center justify-center">
                    <Image
                        src={value.url}
                        alt={value.title}
                        width="100%"
                        height="auto"
                        preview={{
                            visible: previewOpen,
                            onVisibleChange: visible => setPreviewOpen(visible),
                        }}
                        onClick={handlePreview}
                    />
                    {onDelete ? (
                        <Popconfirm
                            title={t("input.image.delete.confirm.title")}
                            description={t("input.image.delete.confirm.description")}
                            okText={t("input.image.delete.confirm.ok")}
                            cancelText={t("input.image.delete.confirm.cancel")}
                            okButtonProps={{ danger: true }}
                            onConfirm={handleDelete}
                        >
                            {deleteButton}
                        </Popconfirm>
                    ) : (
                        deleteButton
                    )}
                </div>
            ) : (
                <Dragger
                    showUploadList={false}
                    beforeUpload={handleUpload}
                    accept={SUPPORTED_MIME_TYPES.join(",")}
                    disabled={isPending}
                    className="size-full border-none bg-transparent"
                >
                    {isPending ? (
                        <div className="flex flex-col items-center justify-center gap-2">
                            <Spin />
                            <Typography.Text className="text-ant-secondary text-ant-sm">
                                {t("input.image.progress")}
                            </Typography.Text>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-2">
                            <Typography.Text className="text-ant-secondary text-ant-sm">
                                {`${t(`input.image.supportedMIME`)} : ${SUPPORTED_MIME_TYPES.map(type => type.split("/")[1]).join(", ")}`}
                            </Typography.Text>
                            <Typography.Text className="text-ant-secondary text-ant-lg">
                                {t("input.image.instructions.text")}
                            </Typography.Text>
                            <Button icon={<Plus01 variant="antd" />}>{t("input.image.instructions.button")}</Button>
                        </div>
                    )}
                </Dragger>
            )}
            {contextHolder}
        </div>
    );
};

export default ImageUploadInput;
